import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
// Reusing CSS from Performance & Click Report (they share the same structure)
import './style/PerformanceReport.css';
import './style/ClickReport.css';

// --- Reusable Custom Date Picker ---
const CustomDatePicker = ({ label, selectedDate, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date(selectedDate || new Date()));
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDayClick = (day) => {
        const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        onChange(newDate.toISOString().split('T')[0]);
        setIsOpen(false);
    };

    const changeMonth = (offset) => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));

    const renderCalendar = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const allSlots = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        return (
            <div className="custom-calendar-unique">
                <div className="cal-header">
                    <button onClick={(e) => { e.stopPropagation(); changeMonth(-1); }}>&lt;</button>
                    <span>{monthNames[month]} {year}</span>
                    <button onClick={(e) => { e.stopPropagation(); changeMonth(1); }}>&gt;</button>
                </div>
                <div className="cal-grid">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d} className="cal-day-name">{d}</div>)}
                    {allSlots.map((day, i) => {
                        if (!day) return <div key={i} className="cal-empty"></div>;
                        const isSelected = new Date(year, month, day).toDateString() === new Date(selectedDate).toDateString();
                        return <div key={i} className={`cal-day ${isSelected ? 'selected' : ''}`} onClick={() => handleDayClick(day)}>{day}</div>;
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="custom-date-wrapper-unique" ref={containerRef}>
            <label className="custom-label">{label}</label>
            <div className={`custom-input-box ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span className="icon-glass">📅</span>
                <span className="date-text">{selectedDate}</span>
            </div>
            {isOpen && renderCalendar()}
        </div>
    );
};

const ConversionReport = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [showColMenu, setShowColMenu] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toISOString().slice(0, 8) + '01',
        endDate: new Date().toISOString().slice(0, 10)
    });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Data ---
    const fetchData = async () => {
        const authToken = getCookie('authToken');
        try {
            setLoading(true);

            // 1. Auth Check
            const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', { action: 'checkAuth', authToken });
            if (!authRes.data.status) {
                navigate('/login');
                return;
            }
            setUser(authRes.data.res);

            // 2. Fetch Conversion Report
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'getConversionReport', // Changed Action
                authToken,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            if (res.data.status) {
                setReportData(res.data.report || []);

                // Dynamic Column Handling
                if (res.data.columns && res.data.columns.length > 0) {
                    setAvailableColumns(res.data.columns);
                    if (visibleColumns.length === 0) setVisibleColumns(res.data.columns);
                } else if (res.data.report.length > 0) {
                    const keys = Object.keys(res.data.report[0]);
                    setAvailableColumns(keys);
                    if (visibleColumns.length === 0) setVisibleColumns(keys);
                }
            }

        } catch (error) {
            toast.error('Failed to load conversion data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [dateRange]);

    // --- Search Logic ---
    const filteredData = useMemo(() => {
        if (!searchQuery) return reportData;
        const lowerQ = searchQuery.toLowerCase();
        return reportData.filter(item =>
            String(item.offer_id).includes(lowerQ) ||
            String(item.offer_name).toLowerCase().includes(lowerQ) ||
            String(item.conv_id).includes(lowerQ) ||
            String(item.click_id).includes(lowerQ)
        );
    }, [reportData, searchQuery]);

    const toggleColumn = (col) => {
        if (visibleColumns.includes(col)) {
            setVisibleColumns(visibleColumns.filter(c => c !== col));
        } else {
            setVisibleColumns([...visibleColumns, col]);
        }
    };

    // --- Export Logic ---
    const handleExport = (type) => {
        if (filteredData.length === 0) return toast.warning('No data to export');

        if (type === 'pdf') {
            window.print();
            return;
        }

        const headers = visibleColumns.join(",");
        const rows = filteredData.map(row =>
            visibleColumns.map(col => `"${row[col] || ''}"`).join(",")
        );
        const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `conversion_report_${dateRange.startDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Exported as ${type === 'excel' ? 'Excel (CSV)' : 'CSV'}`);
    };

    if (loading) return <Loader />;

    return (
        <div className="report-page-wrapper-unique click-report-page">
            <div className="bg-shape-rep shape-r1"></div>
            <div className="bg-shape-rep shape-r2"></div>
            <Navbar name={user?.name} role={user?.role} />

            <div className="report-container-unique">
                <div className="report-header-unique">
                    <h2>Conversion Report</h2>
                    <p>Track your approved, pending, and rejected conversions.</p>
                </div>

                <div className="filters-card-unique">

                    {/* Row 1: Search & Cols */}
                    <div className="search-section-unique">
                        <div className="input-group-glass-unique search-glass">
                            <span className="icon-glass">🔍</span>
                            <input
                                type="text"
                                placeholder="Search Offer, Click ID or Conversion ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <br />
                        <div className="col-toggle-wrapper">
                            <button
                                className={`col-toggle-btn ${showColMenu ? 'active' : ''}`}
                                onClick={() => setShowColMenu(!showColMenu)}
                            >
                                👁 Columns ({visibleColumns.length})
                            </button>
                            {showColMenu && (
                                <div className="col-menu-dropdown">
                                    <div className="col-menu-header">Show/Hide Columns</div>
                                    <div className="col-list-scroll">
                                        {availableColumns.map(col => (
                                            <label key={col} className="col-checkbox-row">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns.includes(col)}
                                                    onChange={() => toggleColumn(col)}
                                                />
                                                <span>{col.replace(/_/g, ' ')}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Dates & Actions */}
                    <div className="controls-row-unique">
                        <CustomDatePicker label="Start Date" selectedDate={dateRange.startDate} onChange={(d) => setDateRange({ ...dateRange, startDate: d })} />
                        <div className="date-arrow-unique">➜</div>
                        <CustomDatePicker label="End Date" selectedDate={dateRange.endDate} onChange={(d) => setDateRange({ ...dateRange, endDate: d })} />

                        <div className="controls-spacer"></div>

                        <div className="export-group-unique">
                            <button className="export-btn excel" onClick={() => handleExport('excel')} title="Export Excel">
                                <span className="exp-icon">📊</span> XL
                            </button>
                            <button className="export-btn csv" onClick={() => handleExport('csv')} title="Export CSV">
                                <span className="exp-icon">📄</span> CSV
                            </button>
                            <button className="export-btn pdf" onClick={() => handleExport('pdf')} title="Print PDF">
                                <span className="exp-icon">🖨</span> PDF
                            </button>
                        </div>

                        <button className="refresh-btn-black-wave" onClick={fetchData} title="Refresh Data">
                            REFRESH 🔄
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="table-card-unique">
                    <div className="table-responsive-unique">
                        <table className="report-table-unique click-table">
                            <thead>
                                <tr>
                                    {visibleColumns.map(col => (
                                        <th key={col}>{col.replace(/_/g, ' ').toUpperCase()}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? (
                                    filteredData.map((row, idx) => (
                                        <tr key={idx} className="clickable-row-unique">
                                            {visibleColumns.map(col => (
                                                <td key={col}>
                                                    {/* Conversion Specific Status Logic */}
                                                    {col === 'status' ? (
                                                        <span className={`status-badge ${row[col]?.toLowerCase()}`}>
                                                            {row[col]}
                                                        </span>
                                                    ) : col === 'payout' || col === 'revenue' ? (
                                                        <span className="highlight-earn">{row[col]}</span>
                                                    ) : (
                                                        row[col]
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={visibleColumns.length} className="no-data-cell">No conversions found.</td>
                                    </tr>
                                )}
                            </tbody>
                            {/* Optional: Add Total Revenue Footer if visible */}
                            {filteredData.length > 0 && visibleColumns.includes('payout') && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={visibleColumns.length} className="text-right earn-total" style={{ paddingRight: '2rem' }}>
                                            Total Revenue: ${filteredData.reduce((sum, item) => sum + parseFloat((item.payout || '0').replace('$', '')), 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConversionReport;